import { useMemo } from "react";
import type { Sale, SaleOrigin } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

const ORIGINS: SaleOrigin[] = ["Facebook", "Google", "TikTok", "Casa", "Recompra"];
const ORIGIN_ICON: Record<SaleOrigin, string> = {
  Facebook: "ri-facebook-circle-line",
  Google: "ri-google-line",
  TikTok: "ri-tiktok-line",
  Casa: "ri-home-smile-line",
  Recompra: "ri-repeat-2-line",
};

// CRM ligero: mejores clientes del período y captación por origen.
const CrmPanel = ({ sales }: { sales: Sale[] }) => {
  const topClients = useMemo(() => {
    const map = new Map<string, { name: string; count: number; total: number }>();
    sales.forEach((s) => {
      const key = s.clientId || s.clientName;
      const entry = map.get(key) ?? { name: s.clientName, count: 0, total: 0 };
      entry.count += 1;
      entry.total += s.total;
      map.set(key, entry);
    });
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [sales]);

  const originStats = useMemo(() => {
    const map = new Map<SaleOrigin, { count: number; total: number }>();
    ORIGINS.forEach((o) => map.set(o, { count: 0, total: 0 }));
    sales.forEach((s) => {
      const entry = map.get(s.origin) ?? { count: 0, total: 0 };
      entry.count += 1;
      entry.total += s.total;
      map.set(s.origin, entry);
    });
    return map;
  }, [sales]);

  const grandTotal = sales.reduce((acc, s) => acc + s.total, 0);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <div className="xl:col-span-2 glass-panel rounded-lg p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
            <i className="ri-user-heart-line" />
          </span>
          <h3 className="editable-title text-base font-bold">Mejores clientes del período</h3>
        </div>
        {topClients.length === 0 ? (
          <p className="text-xs text-foreground-600">Aún no hay ventas registradas en este período.</p>
        ) : (
          <div className="space-y-2">
            {topClients.map((c, i) => (
              <div
                key={c.name}
                className="flex items-center justify-between gap-3 rounded-md bg-background-200/30 px-3 py-2"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500/25 text-primary-100 text-[11px] font-bold shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-sm text-foreground-900 truncate">{c.name}</span>
                </span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="text-[11px] text-foreground-500 whitespace-nowrap">
                    {formatNumber(c.count)} ventas
                  </span>
                  <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                    {formatCOP(c.total)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass-panel rounded-lg p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-8 rounded-md bg-secondary-500/25 text-secondary-200 flex items-center justify-center">
            <i className="ri-radar-line" />
          </span>
          <h3 className="editable-title text-base font-bold">Captación por origen</h3>
        </div>
        <div className="space-y-3">
          {ORIGINS.map((o) => {
            const stat = originStats.get(o) ?? { count: 0, total: 0 };
            const pct = grandTotal > 0 ? (stat.total / grandTotal) * 100 : 0;
            return (
              <div key={o}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="flex items-center gap-1.5 text-xs text-foreground-700">
                    <i className={`${ORIGIN_ICON[o]} text-accent-300`} /> {o}
                  </span>
                  <span className="text-[11px] text-foreground-500">{pct.toFixed(0)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-background-300/40 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-accent-500 to-primary-400 transition-all duration-700"
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CrmPanel;